import { Component, ChangeDetectionStrategy } from '@angular/core';
import {
    FilterDataStatus,
    FilterLimitingConfig,
    FilterModel,
    FilterOption,
    generateFilterPlaceholder$, MultiFilterModel,
    SearchFilterModel, SelectFilterModel,
} from '@ui/shared/util-filter';
import { Observable } from 'rxjs';
import { SchoolMapFilterDataService } from '../../services/school-map-filter-data';
import { Result, ToResult } from '@team23/ts-result';
import { filterKeys } from '../../definitions/filter-keys';
import { statusCodes } from '../../definitions/status-codes';
import { SchoolMapFilterConfigService } from '../../services/school-map-filter-config';
import { StudentGuideFilterOption } from '../../definitions/student-guide-filter-option';
import {
    DropdownFilterComponent,
    FilterContainerModule, FilterContentBlockComponent,
    MultiSelectFilterComponent,
    TextFilterComponent,
} from '@ui/shared/ui-filter';
import { AsyncPipe } from '@angular/common';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-school-map-filters',
    templateUrl: './school-map-filters.component.html',
    styleUrls: ['./school-map-filters.component.scss'],
    imports: [
        TextFilterComponent,
        FilterContainerModule,
        MultiSelectFilterComponent,
        AsyncPipe,
        DropdownFilterComponent,
        FilterContentBlockComponent,
    ],
})
export class SchoolMapFiltersComponent {

    public readonly searchFilter: FilterModel;
    public readonly typesFilter: FilterModel;
    public readonly educationalBranchesFilter: FilterModel;
    public readonly careServicesFilter: FilterModel;
    public readonly studentGuidesFilter: FilterModel;
    public readonly measureTypesFilter: FilterModel;
    public readonly measureStatusFilter: FilterModel;

    public readonly typesOptions$: Observable<Result<Array<FilterOption>, FilterDataStatus>>;
    public readonly educationalBranchesOptions$: Observable<Result<Array<FilterOption>, FilterDataStatus>>;
    public readonly careServicesOptions$: Observable<Result<Array<FilterOption>, FilterDataStatus>>;
    public readonly measureTypesOptions$: Observable<Result<Array<FilterOption>, FilterDataStatus>>;
    public readonly measureStatusOptions: Result<Array<FilterOption>, FilterDataStatus>;
    public readonly studentGuidesOptions: Result<Array<FilterOption>, FilterDataStatus>;

    public readonly educationalBranchesLimitingConfig$: Observable<FilterLimitingConfig>;
    public readonly careServicesLimitingConfig$: Observable<FilterLimitingConfig>;

    public readonly typesFilterPlaceholder$: Observable<string>;
    public readonly educationalBranchesFilterPlaceholder$: Observable<string>;
    public readonly careServicesFilterPlaceholder$: Observable<string>;
    public readonly measureTypesFilterPlaceholder$: Observable<string>;
    public readonly measureStatusFilterPlaceholder: string;

    public readonly filterOptionsFallback = ToResult.fromErrorKey(FilterDataStatus.Loading);

    constructor(
        private readonly _filterDataService: SchoolMapFilterDataService,
        filterConfigService: SchoolMapFilterConfigService,
    ) {
        this.typesOptions$ = _filterDataService.typesOptions$;
        this.educationalBranchesOptions$ = _filterDataService.educationalBranchesOptions$;
        this.careServicesOptions$ = _filterDataService.careServicesOptions$;
        this.measureTypesOptions$ = _filterDataService.measureTypesOptions$;
        this.measureStatusOptions = ToResult.fromData(
            statusCodes.map(status => ({ value: status, label: status })),
        );
        this.studentGuidesOptions = ToResult.fromData([
            { value: StudentGuideFilterOption.Yes, label: 'Ja' },
            { value: StudentGuideFilterOption.No, label: 'Nein' },
        ]);

        this.educationalBranchesLimitingConfig$ = filterConfigService.educationalBranchesLimitingConfig$;
        this.careServicesLimitingConfig$ = filterConfigService.careServicesLimitingConfig$;

        this.typesFilterPlaceholder$ = generateFilterPlaceholder$(this.typesOptions$);
        this.educationalBranchesFilterPlaceholder$ = generateFilterPlaceholder$(this.educationalBranchesOptions$);
        this.careServicesFilterPlaceholder$ = generateFilterPlaceholder$(this.careServicesOptions$);
        this.measureTypesFilterPlaceholder$ = generateFilterPlaceholder$(this.measureTypesOptions$);
        this.measureStatusFilterPlaceholder = `z.B. ${ statusCodes[0] }`;

        this.searchFilter = new SearchFilterModel(filterKeys.textSearch, 'Suchbegriff', []);
        this.typesFilter = new MultiFilterModel(filterKeys.types, 'Schultyp', []);
        this.educationalBranchesFilter = new MultiFilterModel(filterKeys.educationalBranches, 'Schwerpunkt / Bildungszweige', []);
        this.careServicesFilter = new MultiFilterModel(filterKeys.careServices, 'Ganztagsbildungsangebot', []);
        this.studentGuidesFilter = new SelectFilterModel(filterKeys.studentGuides, 'Schulweghelfer anzeigen', []);
        this.measureTypesFilter = new MultiFilterModel(filterKeys.measureTypes, 'Sanierungsmaßnahme', []);
        this.measureStatusFilter = new MultiFilterModel(filterKeys.measureStatus, 'Status der Maßnahme', []);
    }

    public reloadFilterOptions(): void {
        this._filterDataService.reloadErrored();
    }
}
